import { IS_PRODUCTION } from 'constants/isDevelopment'
import { getCookieConsentValue } from 'react-cookie-consent'

const PIXEL_ID = '3qALuqMB7Rvuc1ErgwnkBE'
const PIXEL_SCRIPT_ID = 'medleve-openai-measurement-pixel'
const PIXEL_SCRIPT_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'

type PixelCommand = ((...args: unknown[]) => void) & { q?: unknown[][] }
type PixelWindow = Window & { oaiq?: PixelCommand }
const PURCHASE_STORAGE_KEY = 'medleve-openai-recorded-purchases'
const recordedPurchases = new Set<string>()

export const isMedleveHostname = (hostname: string): boolean => /^(www\.)?medleve\.com$/i.test(hostname)

const initializeMedlevePixel = (): PixelCommand | null => {
  if (
    typeof window === 'undefined' ||
    !IS_PRODUCTION ||
    !isMedleveHostname(window.location.hostname) ||
    getCookieConsentValue() !== 'true'
  )
    return null

  const pixelWindow = window as PixelWindow

  if (!pixelWindow.oaiq) {
    const queue = ((...args: unknown[]) => {
      queue.q?.push(args)
    }) as PixelCommand
    queue.q = []
    pixelWindow.oaiq = queue
  }

  if (!document.getElementById(PIXEL_SCRIPT_ID)) {
    pixelWindow.oaiq('consent', true)
    pixelWindow.oaiq('init', { pixelId: PIXEL_ID })

    const script = document.createElement('script')
    script.id = PIXEL_SCRIPT_ID
    script.async = true
    script.src = PIXEL_SCRIPT_URL
    document.head.appendChild(script)
  }

  return pixelWindow.oaiq
}

// Public landing layouts only. Never attach patient/clinical information.
export const trackMedlevePageView = (): void => {
  initializeMedlevePixel()?.('measure', 'page_viewed', { type: 'contents' })
}

// Only call from a confirmed-payment result, never from PIX creation or a
// thank-you URL alone. The order key stays in this browser, not in the payload.
export const trackMedlevePurchase = (
  source: 'consultation' | 'package' | 'weight',
  orderId: string | number | null | undefined,
  paymentConfirmed: boolean
): boolean => {
  try {
    if (!paymentConfirmed || orderId == null || !String(orderId).trim()) return false
    if (typeof orderId === 'number' && (!Number.isFinite(orderId) || orderId <= 0)) return false
    const key = `${source}:${orderId}`
    if (recordedPurchases.has(key)) return false

    let previous: string[] = []
    try {
      const stored: unknown = JSON.parse(window.localStorage.getItem(PURCHASE_STORAGE_KEY) || '[]')
      if (Array.isArray(stored)) previous = stored.filter((item): item is string => typeof item === 'string')
    } catch {
      // Storage may be unavailable. In-memory deduplication still applies.
    }
    if (previous.includes(key)) return false

    const pixel = initializeMedlevePixel()
    if (!pixel) return false

    pixel('measure', 'custom', { type: 'custom' }, { custom_event_name: 'purchase' })
    recordedPurchases.add(key)
    try {
      window.localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify([...previous, key].slice(-100)))
    } catch {
      // Analytics must never turn an approved payment into a checkout error.
    }
    return true
  } catch {
    return false
  }
}
